import {
  Box,
  HStack,
  IconButton,
  Text,
  Accordion,
  Icon,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { NodeThumbnail } from "./NodeThumbnail";
import { NodeTreatments } from "./NodeTreatments";
import { ReactNode, useMemo } from "react";
import { ViewScoreSmall } from "../../../../../coding/views/ViewScoreSmall";
import { SelectedNodeInfo } from "../../coding/shared/SelectedNodeInfo";
import { LifeCycleStage } from "../../../../../cra/models/LifeCycleStage";
import { useStores } from "../../../../../shared/stores/RootStore";
import { ArrowBackIcon } from "../../../../../shared/views/icons";
import { AttributeList } from "./attributes/AttributeList";

const i18prefix = "map:nodeSummary";

const CraNodeDetailBase: React.FC = () => {
  const { craWayStore, craForecastStore, craNodeStore, mapStore } = useStores();

  const selectedCraWay = craWayStore.getSelected();
  const node = craNodeStore.getSelected()!;
  const nodes = selectedCraWay
    ? craNodeStore.nodesByWayId.get(selectedCraWay?.id)
    : [];

  const { appliedTreatments } = useMemo(() => {
    const { computedValues } = craForecastStore;
    if (!computedValues) {
      return {
        appliedTreatments: [],
      };
    } else {
      const originalNode = craNodeStore.getOriginal(node.id)!;
      return {
        appliedTreatments: originalNode.treatments?.filter(
          (treatment) =>
            !node.treatments?.find((t) => t.type === treatment.type),
        ),
      };
    }
    // eslint-disable-next-line
  }, [node]);

  const codingStageDone =
    selectedCraWay?.lifeCycleStage === LifeCycleStage.Review ||
    selectedCraWay?.lifeCycleStage === LifeCycleStage.Completed;

  return (
    <>
      <HStack px={2}>
        <Box flexShrink={0} width="40px">
          <IconButton
            size="sm"
            variant="ghost"
            aria-label={t(`${i18prefix}.clearSelection`)}
            onClick={() => {
              craNodeStore.clearSelected();
              mapStore.recenterMap();
            }}
            title={t(`${i18prefix}.clearSelection`)}
          >
            <Icon as={ArrowBackIcon} fontSize="lg" />
          </IconButton>
        </Box>
        <Text flexGrow={1} p={2} fontWeight="semibold" textAlign="center">
          {t(`${i18prefix}.title`, {
            position: nodes && nodes?.findIndex((n) => n.id === node.id) + 1,
            total: nodes?.length,
          })}
        </Text>
        <Box flexShrink={0} width="40px" />
      </HStack>

      <NodeThumbnail />

      <Box overflow="auto">
        <Accordion.Root collapsible multiple defaultValue={allAccordionItems}>
          <CustomAccordionItem text="Details">
            <SelectedNodeInfo />
          </CustomAccordionItem>

          {node.score && (
            <CustomAccordionItem text="Score">
              <ViewScoreSmall score={node.score} />
            </CustomAccordionItem>
          )}

          {codingStageDone && !!node.treatments && (
            <CustomAccordionItem text="Treatments">
              <NodeTreatments
                treatments={node.treatments}
                appliedTreatments={appliedTreatments}
              />
            </CustomAccordionItem>
          )}

          {codingStageDone && (
            <CustomAccordionItem text="Attributes">
              <AttributeList />
            </CustomAccordionItem>
          )}
        </Accordion.Root>
      </Box>
    </>
  );
};

export const CraNodeDetail = observer(CraNodeDetailBase);

const allAccordionItems = ["Details", "Score", "Treatments", "Attributes"];

const CustomAccordionItem: React.FC<{
  text: string;
  children: ReactNode;
}> = ({ text, children }) => (
  <Accordion.Item value={text}>
    <Accordion.ItemTrigger px={4}>
      <Text flex="1" textAlign="left">
        {text}
      </Text>
      <Accordion.ItemIndicator />
    </Accordion.ItemTrigger>
    <Accordion.ItemContent>
      <Accordion.ItemBody p={4}>{children}</Accordion.ItemBody>
    </Accordion.ItemContent>
  </Accordion.Item>
);